/**
 * Role Permission Utilities
 *
 * Provides role hierarchy and permission checking for frontend visibility.
 * IMPORTANT: This is for UI visibility only - NOT for security enforcement.
 * Backend middleware handles actual security.
 *
 * @module utils/rolePermissions
 */

import type { WorkspaceRole } from "@/types";

/**
 * Role permission levels (hierarchical)
 * Must match backend ROLE_LEVELS
 */
export const ROLE_LEVELS: Record<WorkspaceRole, number> = {
  viewer: 1,
  analyst: 2,
  admin: 3,
};

/**
 * Valid role values
 */
export const VALID_ROLES: WorkspaceRole[] = ["admin", "analyst", "viewer"];

/**
 * Check if a role is valid
 */
export const isValidRole = (role: string): boolean => {
  if (!role) return false;
  return VALID_ROLES.includes(role.toLowerCase() as WorkspaceRole);
};

/**
 * Get the permission level for a user role
 * @returns Permission level (0 for invalid/unknown roles)
 */
export const getUserRoleLevel = (userRole: string): number => {
  if (!userRole) return 0;
  return ROLE_LEVELS[userRole.toLowerCase() as WorkspaceRole] ?? 0;
};

/**
 * Check if user role has permission to access content requiring specific roles
 * Uses hierarchical comparison (admin > analyst > viewer)
 *
 * @example
 * hasPermission('admin', ['admin']); // true
 * hasPermission('admin', ['analyst']); // true (hierarchical)
 * hasPermission('analyst', ['admin']); // false
 * hasPermission('viewer', ['admin', 'analyst']); // false
 */
export const hasPermission = (
  userRole: string,
  requiredRoles: WorkspaceRole[]
): boolean => {
  if (
    !userRole ||
    !requiredRoles ||
    !Array.isArray(requiredRoles) ||
    requiredRoles.length === 0
  ) {
    return false;
  }

  const userLevel = getUserRoleLevel(userRole);
  const minimumRequiredLevel = Math.min(
    ...requiredRoles.map(
      (role) => ROLE_LEVELS[role.toLowerCase() as WorkspaceRole] ?? Infinity
    )
  );

  return userLevel >= minimumRequiredLevel;
};

/**
 * Check if current user is an admin
 */
export const isAdmin = (userRole: string): boolean => {
  return userRole?.toLowerCase() === "admin";
};

/**
 * Check if current user is analyst or admin
 */
export const isAnalystOrAbove = (userRole: string): boolean => {
  const level = getUserRoleLevel(userRole);
  return level >= ROLE_LEVELS.analyst;
};

/**
 * Check if user is authenticated (has any valid role)
 */
export const isAuthenticated = (userRole: string): boolean => {
  return isValidRole(userRole);
};

export default {
  ROLE_LEVELS,
  VALID_ROLES,
  isValidRole,
  getUserRoleLevel,
  hasPermission,
  isAdmin,
  isAnalystOrAbove,
  isAuthenticated,
};
